import React, { createContext, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { recentApiError } from "../api/apiErrorDetail";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { pushToast } from "./Toast";
import { pushConfirm } from "./ConfirmDialog";
import { CloseOutlined } from "./admin-icons";

type AnyRecord = Record<string, any>;

export type ColumnsType<T = AnyRecord> = Array<{
  title?: ReactNode;
  dataIndex?: string | string[];
  key?: string;
  width?: number | string;
  fixed?: string;
  render?: (value: any, record: T, index: number) => ReactNode;
  [key: string]: any;
}>;

export type TableProps<T = AnyRecord> = {
  dataSource?: T[];
  columns?: ColumnsType<T>;
  rowKey?: string | ((record: T) => string);
  loading?: boolean;
  pagination?: false | AnyRecord;
  size?: string;
  scroll?: AnyRecord;
  rowClassName?: string | ((record: T, index: number) => string);
  onRow?: (record: T, index?: number) => React.HTMLAttributes<HTMLTableRowElement>;
  components?: AnyRecord;
  style?: CSSProperties;
  sticky?: any;
  footer?: any;
  [key: string]: any;
};

export type MenuProps = { items?: Array<{ key: string; label?: ReactNode; icon?: ReactNode; danger?: boolean; disabled?: boolean; onClick?: () => void; [key: string]: any }> };
export type RangePickerProps = AnyRecord;
export type Color = { toHexString?: () => string } | string;

const C = {
  brand: "#F97316",
  border: "#E5E7EB",
  muted: "#6B7280",
  text: "#111827",
  bg: "#F8FAFC",
  surface: "#FFFFFF",
};

const UI = {
  button: "admin-button inline-flex min-h-9 cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-[#e5e7eb] bg-white px-3.5 py-2 font-extrabold text-[#111827] transition hover:border-[#fed7aa] hover:bg-[#fff7ed] hover:text-[#ea580c] disabled:cursor-not-allowed disabled:opacity-65",
  inputWrap: "admin-input-wrap admin-focus-wrap inline-flex min-h-[38px] w-full items-center gap-2 rounded-[10px] border border-[#e5e7eb] bg-white px-2.5 transition focus-within:border-[#f97316] focus-within:ring-4 focus-within:ring-[#f97316]/10",
  input: "admin-input min-h-9 w-full rounded-[10px] border-0 bg-white text-[#111827] outline-none",
  textarea: "admin-textarea w-full resize-y rounded-[10px] border border-[#e5e7eb] bg-white p-2.5 text-[#111827] outline-none",
  dateInput: "min-h-10 w-full rounded-[10px] border border-[#e5e7eb] bg-white px-3 text-sm font-bold text-[#111827] outline-none transition focus:border-[#f97316] focus:ring-4 focus:ring-[#f97316]/10",
  tableWrap: "admin-table-wrap w-full overflow-x-auto",
  table: "admin-table w-full border-separate border-spacing-0 bg-white text-sm [&_th]:border-b [&_th]:border-[#e5e7eb] [&_th]:bg-[#f8fafc] [&_th]:px-3.5 [&_th]:py-3 [&_th]:text-left [&_th]:text-[11px] [&_th]:font-black [&_th]:uppercase [&_th]:text-[#64748b] [&_td]:border-b [&_td]:border-[#f1f5f9] [&_td]:px-3.5 [&_td]:py-[13px] [&_td]:align-middle [&_td]:text-[#111827] [&_tr:hover_td]:bg-[#fff7ed]",
  modalMask: "admin-overlay admin-modal-mask fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden bg-[rgba(15,23,42,0.42)] p-6 max-[1280px]:items-stretch max-[1280px]:p-4",
  modalPanel: "admin-modal-panel flex max-h-[min(92dvh,860px)] max-w-[min(96vw,720px)] flex-col overflow-hidden rounded-[18px] bg-white p-[22px] shadow-[0_24px_70px_rgba(15,23,42,0.24)] max-[1280px]:m-auto max-[1280px]:!w-[min(100%,680px)] max-[1280px]:rounded-2xl max-[1280px]:p-[18px]",
  drawer: "admin-drawer fixed bottom-0 right-0 top-0 flex max-w-[92vw] flex-col rounded-l-[18px] rounded-r-none bg-white p-[22px] shadow-[0_24px_70px_rgba(15,23,42,0.24)]",
  modalFooter: "admin-modal-footer mt-5 flex shrink-0 justify-end gap-2.5 border-t border-[#f1f5f9] pt-3.5 max-[1280px]:mt-3.5 max-[1280px]:flex-wrap [&_button]:max-[1280px]:flex-[1_1_140px]",
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function getValue(record: AnyRecord, dataIndex: string | string[] | undefined) {
  if (!dataIndex) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  return path.reduce<any>((value, key) => value?.[key], record);
}

export function Table<T extends AnyRecord>(props: TableProps<T>) {
  const { t } = useTranslation();
  const { dataSource = [], columns = [], rowKey = "id", loading, rowClassName, onRow, style, pagination } = props;
  const [internalPage, setInternalPage] = useState(1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<{ overflow: boolean; atEnd: boolean }>({ overflow: false, atEnd: true });
  const keyFor = (row: T, index: number) => typeof rowKey === "function" ? rowKey(row) : String(row[rowKey] ?? index);
  const paginationConfig = pagination === false ? null : (pagination ?? {});
  const controlledPage = paginationConfig?.current ? Number(paginationConfig.current) : undefined;
  const pageSize = Number(paginationConfig?.pageSize ?? paginationConfig?.defaultPageSize ?? 10);
  const total = Number(paginationConfig?.total ?? dataSource.length);
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const currentPage = Math.min(totalPages, Math.max(1, controlledPage ?? internalPage));
  const isServerPaginated = paginationConfig?.total != null || paginationConfig?.current != null;
  const offset = paginationConfig ? (currentPage - 1) * pageSize : 0;
  const pageRows = paginationConfig
    ? isServerPaginated
      ? dataSource
      : dataSource.slice(offset, offset + pageSize)
    : dataSource;
  const isEmpty = dataSource.length === 0 && !loading;
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(total, offset + pageRows.length);

  useEffect(() => {
    if (!controlledPage && internalPage > totalPages) setInternalPage(totalPages);
  }, [controlledPage, internalPage, totalPages]);

  // Detect horizontal overflow so we can show a visible "more columns this
  // way" affordance — a plain `overflow-x-auto` wrapper is scrollable but
  // gives zero visual hint on mobile, so clipped columns (e.g. an Enabled
  // switch) go completely undiscovered without this.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const overflow = el.scrollWidth > el.clientWidth + 1;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setScrollState({ overflow, atEnd });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [dataSource, columns, isEmpty]);

  const goToPage = (page: number) => {
    const next = Math.min(totalPages, Math.max(1, page));
    if (!controlledPage) setInternalPage(next);
    paginationConfig?.onChange?.(next, pageSize);
  };

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    return Array.from(pages).filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
  }, [currentPage, totalPages]);

  const showScrollHint = scrollState.overflow && !scrollState.atEnd;

  return (
    <div style={style}>
      <div className="relative">
        <div ref={wrapRef} className={cx(UI.tableWrap, isEmpty && "overflow-x-hidden")}>
        {loading ? <Spin /> : null}
        <table
          className={cx(UI.table, isEmpty && "min-w-full")}
          style={{ minWidth: columns.reduce((sum, col) => sum + (Number(col.width) || 120), 0) }}
        >
          <thead>
            <tr>{columns.map((col, i) => <th key={col.key ?? String(col.dataIndex ?? i)} style={{ width: col.width }}>{col.title}</th>)}</tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr><td colSpan={columns.length || 1} className="!text-center px-7 py-7 text-slate-500"><div className="admin-table-empty">{t("common.noData")}</div></td></tr>
            ) : pageRows.map((row, rowIndex) => {
              const absoluteIndex = isServerPaginated ? rowIndex : rowIndex + offset;
              const rowProps = onRow?.(row, absoluteIndex) ?? {};
              const className = typeof rowClassName === "function" ? rowClassName(row, absoluteIndex) : rowClassName;
              return (
                <tr key={keyFor(row, absoluteIndex)} {...rowProps} className={className}>
                  {columns.map((col, colIndex) => {
                    const value = getValue(row, col.dataIndex);
                    return <td key={col.key ?? String(col.dataIndex ?? colIndex)} style={{ width: col.width }}>{col.render ? col.render(value, row, absoluteIndex) : value}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {showScrollHint && (
          <>
            <div className="pointer-events-none absolute bottom-0 right-0 top-0 w-10 bg-gradient-to-l from-white to-transparent" aria-hidden="true" />
            <div className="pointer-events-none absolute bottom-1.5 right-2 flex items-center gap-1 rounded-full bg-[#191c1d]/80 px-2 py-0.5 text-[10px] font-black text-white">
              {t("common.swipeForMore", "Swipe →")}
            </div>
          </>
        )}
      </div>
      {paginationConfig && totalPages > 1 ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-orange-100 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.05)]">
          <span className="text-xs font-extrabold text-slate-500">
            {typeof paginationConfig.showTotal === "function"
              ? paginationConfig.showTotal(total, [rangeStart, rangeEnd])
              : t("common.tablePageRange", { start: rangeStart, end: rangeEnd, total })}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="inline-flex h-9 min-w-9 items-center justify-center rounded-xl border border-orange-100 bg-white px-3 text-xs font-black text-[#534434] transition hover:border-orange-200 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>{t("common.previousPage")}</button>
            {pageNumbers.map((page, index) => (
              <React.Fragment key={page}>
                {index > 0 && page - (pageNumbers[index - 1] ?? page) > 1 ? <span className="px-1 text-xs font-black text-slate-400">...</span> : null}
                <button
                  type="button"
                  className={cx(
                    "inline-flex h-9 min-w-9 max-sm:h-11 items-center justify-center rounded-xl border px-3 text-xs font-black transition",
                    page === currentPage
                      ? "!border-orange-500 !bg-orange-500 !text-white shadow-[0_8px_18px_rgba(249,115,22,0.24)] hover:!border-orange-500 hover:!bg-orange-500 hover:!text-white"
                      : "border-orange-100 bg-white text-[#534434] hover:border-orange-200 hover:bg-orange-50",
                  )}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              </React.Fragment>
            ))}
            <button type="button" className="inline-flex h-9 min-w-9 items-center justify-center rounded-xl border border-orange-100 bg-white px-3 text-xs font-black text-[#534434] transition hover:border-orange-200 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-40" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>{t("common.nextPage")}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function Button({ children, type, danger, loading, icon, block, style, disabled, htmlType, className = "", ...rest }: any) {
  const iconOnly = !!icon && !children;
  return (
    <button
      {...rest}
      type={htmlType ?? "button"}
      disabled={disabled || loading}
      className={cx(
        UI.button,
        type === "primary" && "admin-button-primary border-transparent !bg-[#f97316] !text-white shadow-[0_10px_22px_rgba(249,115,22,0.22)] hover:border-transparent hover:!bg-[#ea580c] hover:!text-white",
        danger && "border-transparent !bg-[#dc2626] !text-white shadow-[0_10px_22px_rgba(220,38,38,0.18)] hover:border-transparent hover:!bg-[#b91c1c] hover:!text-white",
        block && "w-full",
        iconOnly && "size-9 min-h-9 min-w-9 max-sm:h-11 p-0 leading-none [&>svg]:block [&>svg]:shrink-0 max-sm:size-11 max-sm:min-h-11 max-sm:min-w-11",
        className,
      )}
      style={style}
    >
      {loading ? "..." : icon}{children}
    </button>
  );
}

function BaseInput({ prefix, suffix, style, value, onChange, allowClear, addonBefore, addonAfter, ...rest }: any) {
  return (
    <span className={UI.inputWrap} style={style}>
      {addonBefore}
      {prefix}
      <input {...rest} value={value ?? ""} onChange={onChange} className={UI.input} />
      {suffix}
      {addonAfter}
    </span>
  );
}

export const Input: any = Object.assign(BaseInput, {
  Password: (props: any) => <BaseInput {...props} type="password" />,
  TextArea: ({ style, value, onChange, rows = 4, showCount, allowClear, autoSize, ...rest }: any) => <textarea {...rest} rows={rows} value={value ?? ""} onChange={onChange} className={UI.textarea} style={style} />,
  Search: (props: any) => <BaseInput {...props} type="search" />,
});

export function InputNumber({ value, onChange, style, prefix, ...rest }: any) {
  return (
    <span className={UI.inputWrap} style={style}>
      {prefix && <span className="shrink-0 text-sm font-extrabold text-[#6b7280]">{prefix}</span>}
      <input {...rest} type="number" value={value ?? ""} onChange={(e) => onChange?.(e.target.value === "" ? null : Number(e.target.value))} className={UI.input} />
    </span>
  );
}

export function Select({ value, onChange, options, children, style, placeholder, allowClear, mode, className = "", ...rest }: any) {
  const childOptions = React.Children.toArray(children).map((child: any) => child?.props).filter(Boolean);
  const allOptions = options ?? childOptions;

  if (mode === "multiple") {
    return (
      <MultiSelect
        value={value} onChange={onChange} options={allOptions}
        style={style} placeholder={placeholder} allowClear={allowClear} className={className}
      />
    );
  }

  return (
    <span className={cx("admin-focus-wrap relative inline-flex min-h-11 w-full items-center rounded-xl border border-[#e7e1db] bg-white shadow-[0_8px_20px_rgba(25,28,29,0.04)] transition focus-within:border-[#ea580c] focus-within:ring-4 focus-within:ring-[#ea580c]/10", className)} style={style}>
      <select {...rest} value={value ?? ""} onChange={(e) => onChange?.(e.target.value || undefined)} className="h-11 w-full cursor-pointer appearance-none rounded-xl border-0 bg-transparent py-0 pl-3.5 pr-10 text-sm font-bold text-[#191c1d] outline-none disabled:cursor-not-allowed disabled:bg-[#f3f4f5] disabled:text-[#867461]">
        {(placeholder || allowClear) && <option value="">{placeholder ?? "全部"}</option>}
        {allOptions?.map((option: any) => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label ?? option.children}</option>)}
      </select>
      <svg className="pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-[#a14000]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
Select.Option = ({ children }: any) => <>{children}</>;

// mode="multiple" needs real array semantics — a native <select> can only ever
// carry one scalar value, so it silently dropped every selection beyond the
// first (and React warned about passing an array where a scalar was expected).
// This renders a checklist popover instead and always calls onChange with an array.
function MultiSelect({ value, onChange, options, style, placeholder, allowClear, className = "" }: any) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});
  const selected: string[] = Array.isArray(value) ? value : [];

  const updatePopoverPosition = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    setPopoverStyle({ position: "fixed", top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 3000 });
  };

  // Rendered via a portal (like Modal/Drawer/Dropdown above) and positioned
  // from the trigger's getBoundingClientRect — a plain `position: absolute`
  // popover gets clipped when the trigger sits near the bottom of a
  // scrollable ancestor (e.g. a Modal body with overflow-y: auto).
  useEffect(() => {
    if (!open) return;
    updatePopoverPosition();
    const onDocClick = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node) || popoverRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    // Scroll events don't bubble, but a capture-phase window listener still
    // receives them from any descendant — including the popover's own
    // overflow-y-auto list. Without this guard, scrolling the option list
    // itself closes it before the browser paints any movement, making a
    // long options list look permanently stuck instead of scrollable.
    const closeOnOutsideScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", closeOnOutsideScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", closeOnOutsideScroll, true);
    };
  }, [open]);

  const toggle = (val: string) => {
    const next = selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val];
    onChange?.(next);
  };
  const selectedLabels = options.filter((o: any) => selected.includes(o.value)).map((o: any) => o.label ?? o.children);

  return (
    <div ref={ref} className={cx("relative w-full", className)} style={style}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="admin-focus-control flex min-h-11 w-full cursor-pointer flex-wrap items-center gap-1.5 rounded-xl border border-[#e7e1db] bg-white px-3.5 py-2 text-left text-sm font-bold text-[#191c1d] shadow-[0_8px_20px_rgba(25,28,29,0.04)] transition focus:border-[#ea580c] focus:ring-4 focus:ring-[#ea580c]/10"
      >
        {selectedLabels.length === 0 ? (
          <span className="text-[#867461]">{placeholder ?? "请选择"}</span>
        ) : (
          selectedLabels.map((label: ReactNode, i: number) => (
            <span key={i} className="inline-flex items-center rounded-md bg-[#fff7ed] px-2 py-0.5 text-xs font-bold text-[#c2410c]">{label}</span>
          ))
        )}
        {allowClear && selected.length > 0 && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange?.([]); }}
            className="ml-auto cursor-pointer text-[#867461] hover:text-[#191c1d]"
          >
            ✕
          </span>
        )}
      </button>
      {open && createPortal(
        <div ref={popoverRef} className="max-h-64 overflow-y-auto rounded-xl border border-[#e7e1db] bg-white p-1.5 shadow-[0_16px_40px_rgba(25,28,29,0.12)]" style={popoverStyle}>
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#867461]">暂无选项</div>
          ) : (
            options.map((option: any) => (
              <label key={option.value} className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold text-[#191c1d] hover:bg-[#fff7ed]">
                <input
                  type="checkbox"
                  className="size-4 accent-[#f97316]"
                  checked={selected.includes(option.value)}
                  disabled={option.disabled}
                  onChange={() => toggle(option.value)}
                />
                {option.label ?? option.children}
              </label>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

export function Switch({ checked, onChange, disabled, style }: any) {
  return <button type="button" disabled={disabled} onClick={() => onChange?.(!checked)} className={cx("h-6 w-[42px] cursor-pointer rounded-full border-0 bg-[#cbd5e1] p-[3px] disabled:cursor-not-allowed disabled:opacity-60", checked && "bg-[#f97316]")} style={style}><span className={cx("block size-[18px] rounded-full bg-white transition-transform duration-[180ms]", checked && "translate-x-[18px]")} /></button>;
}

export function Checkbox({ checked, onChange, children, style, className = "" }: any) {
  return <label className={cx("inline-flex cursor-pointer items-center gap-2 text-sm font-bold text-[#191c1d]", className)} style={style}><input type="checkbox" className="size-4 accent-[#f97316]" checked={!!checked} onChange={(e) => onChange?.(e)} />{children}</label>;
}

export function DatePicker({ value, onChange, style, placeholder, className = "" }: any) {
  const formatted = value?.format?.("YYYY-MM-DD") ?? value ?? "";
  return <input type="date" value={formatted} onChange={(e) => onChange?.(e.target.value ? dayjs(e.target.value) : null)} className={cx(UI.dateInput, className)} style={style} placeholder={placeholder} />;
}
DatePicker.RangePicker = ({ value, onChange, style, className = "" }: any) => {
  const formatted = Array.isArray(value) ? (value[0]?.format?.("YYYY-MM-DD") ?? value[0] ?? "") : "";
  return <input type="date" value={formatted} onChange={(e) => { const dates = [e.target.value, e.target.value]; onChange?.(dates, dates); }} className={cx(UI.dateInput, className)} style={style} />;
};

export function TimePicker({ value, onChange, style }: any) {
  const v = typeof value === "string" ? dayjs(value, "HH:mm") : value;
  const formatted = v?.isValid?.() ? v.format("HH:mm") : "";
  return (
    <input
      type="time"
      value={formatted}
      onChange={(e) => onChange?.(e.target.value ? dayjs(e.target.value, "HH:mm") : null)}
      className={cx(UI.dateInput, "min-w-[128px] !px-2")}
      style={style}
    />
  );
}

export function Card({ children, title, extra, loading, style, styles, className = "" }: any) {
  return (
    <section className={cx("admin-card overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white shadow-[0_8px_22px_rgba(15,23,42,0.05)]", className)} style={style}>
      {(title || extra) && (
        <div className="flex items-center justify-between border-b border-[#e5e7eb] px-[18px] py-3.5 text-sm font-extrabold text-[#191c1d]" style={styles?.header}>
          <span>{title}</span>
          {extra}
        </div>
      )}
      <div className="p-[18px]" style={styles?.body}>{loading ? <Spin /> : children}</div>
    </section>
  );
}

export function Spin({ size }: any) {
  return <span className={cx("inline-block size-[22px] rounded-full border-[3px] border-[#fed7aa] border-t-[#f97316] animate-spin", size === "large" && "size-9")} />;
}

export function Divider({ style }: any) {
  return <div className="my-4 h-px w-full bg-[#e5e7eb]" style={style} />;
}

export function Modal({ open, visible, title, children, onCancel, onOk, footer, okText = "Confirm", cancelText = "Cancel", width, confirmLoading, okButtonProps, cancelButtonProps, maskClosable = false }: any) {
  const { t } = useTranslation();
  if (!(open ?? visible)) return null;
  const node = (
    <div className={UI.modalMask} onClick={() => { if (maskClosable) onCancel?.(); }}>
      <div className={UI.modalPanel} style={{ width }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex shrink-0 items-start justify-between gap-3">
          <div className="min-w-0 flex-1">{title}</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onCancel}
            className="-m-1 flex size-7 max-sm:size-9 shrink-0 items-center justify-center rounded-lg text-[#94a3b8] transition hover:bg-[#f1f5f9] hover:text-[#111827]"
          >
            <CloseOutlined style={{ fontSize: 16 }} />
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto pr-1">{children}</div>
        {footer !== null && <div className={UI.modalFooter}>{footer ?? <><Button onClick={onCancel} {...cancelButtonProps}>{cancelText === "Cancel" ? t("common.cancel") : cancelText}</Button><Button type="primary" loading={confirmLoading} onClick={onOk} {...okButtonProps}>{okText === "Confirm" ? t("common.confirm") : okText}</Button></>}</div>}
      </div>
    </div>
  );
  return typeof document === "undefined" ? node : createPortal(node, document.body);
}

Modal.confirm = ({ title, content, onOk, okText, cancelText, danger }: any) => {
  void pushConfirm({ title: title ?? "", description: content, confirmLabel: okText, cancelLabel: cancelText, danger: danger ?? true }).then((ok) => {
    if (ok) void onOk?.();
  });
};

export function Drawer({ open, visible, title, extra, children, onClose, width, footer, placement, afterOpenChange, maskClosable = false, closable = true, closeIcon }: any) {
  const showClose = closable !== false && closeIcon !== false;
  const isOpen = !!(open ?? visible);
  useEffect(() => {
    afterOpenChange?.(isOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  if (!isOpen) return null;
  const node = (
    <div className={UI.modalMask} onClick={() => { if (maskClosable) onClose?.(); }}>
      <aside className={UI.drawer} data-placement={placement} style={{ width }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>{title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {extra}
            {showClose && <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="-m-1 flex size-7 max-sm:size-9 shrink-0 items-center justify-center rounded-lg text-[#94a3b8] transition hover:bg-[#f1f5f9] hover:text-[#111827]"
            >
              <CloseOutlined style={{ fontSize: 16 }} />
            </button>}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer && <div className={UI.modalFooter}>{footer}</div>}
      </aside>
    </div>
  );
  return typeof document === "undefined" ? node : createPortal(node, document.body);
}

export function Dropdown({ children, menu }: any) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<CSSProperties>({});
  const items = menu?.items ?? [];

  const updateMenuPosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Always anchoring below the trigger clips/hides the menu for rows near
    // the bottom of the viewport (e.g. the last row of a table). Flip to
    // opening upward when there isn't enough room below for the estimated
    // menu height.
    const estimatedHeight = items.length * 38 + 12;
    const openUpward = window.innerHeight - rect.bottom < estimatedHeight + 8 && rect.top > estimatedHeight + 8;
    setMenuPosition({
      position: "fixed",
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 8 }
        : { top: rect.bottom + 8 }),
      right: Math.max(12, window.innerWidth - rect.right),
      zIndex: 3000,
    });
  };

  // Close-on-outside-click is done via a `mousedown` + explicit contains()
  // check (same pattern as MultiSelect above) rather than a bubble-phase
  // `window` click listener that depends on the popover/item calling
  // e.stopPropagation() to avoid self-closing. That dependency is fragile —
  // since the popover is rendered through a portal, a native click on an
  // item only reaches this listener if every intermediate handler correctly
  // stops propagation, and any gap there silently swallows the item's own
  // onClick along with the close. mousedown+contains() never has that failure
  // mode: it explicitly ignores clicks that originate inside the trigger or
  // the popover, so an item's click always gets to fire before anything closes it.
  useEffect(() => {
    if (!open) return;

    updateMenuPosition();
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", close, true);

    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  // Focus never reached the popover via Tab because it's teleported (via
  // portal) to the very end of <body>, far from the trigger in DOM order —
  // Tab would walk through every other focusable element on the page first.
  // Moving focus into the menu on open (and trapping Tab within it while
  // open) keeps keyboard use working regardless of DOM position.
  useEffect(() => {
    if (!open) return;
    const firstItem = popoverRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)");
    firstItem?.focus();
  }, [open]);

  const closeAndRefocus = () => {
    setOpen(false);
    triggerRef.current?.querySelector<HTMLElement>("button, [tabindex]")?.focus();
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const focusable = Array.from(popoverRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []);
    const currentIndex = focusable.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") {
      e.preventDefault();
      closeAndRefocus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      focusable[(currentIndex + 1) % focusable.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusable[(currentIndex - 1 + focusable.length) % focusable.length]?.focus();
    } else if (e.key === "Tab") {
      // Trap focus inside the popover while it's open instead of letting Tab
      // escape to unrelated controls elsewhere on the page.
      e.preventDefault();
      const nextIndex = e.shiftKey
        ? (currentIndex - 1 + focusable.length) % focusable.length
        : (currentIndex + 1) % focusable.length;
      focusable[nextIndex]?.focus();
    }
  };

  return (
    <span ref={triggerRef} className="relative inline-flex">
      <span onClick={(e) => { e.stopPropagation(); updateMenuPosition(); setOpen((v) => !v); }}>{children}</span>
      {open && createPortal(
        <div
          ref={popoverRef}
          className="z-[500] min-w-40 max-w-[calc(100vw-24px)] rounded-xl border border-[#e5e7eb] bg-white p-1.5 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
          style={menuPosition}
          onKeyDown={handleMenuKeyDown}
        >
          {items.map((item: any) => (
            <button key={item.key} className="w-full cursor-pointer rounded-[9px] border-0 bg-transparent px-2.5 py-[9px] text-left text-sm font-bold hover:bg-[#fff7ed] hover:text-[#ea580c]" disabled={item.disabled} onClick={() => { item.onClick?.(); setOpen(false); }}>
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
}

export function Popconfirm({ children, title, onConfirm, cancelLabel, confirmLabel }: any) {
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        void pushConfirm({ title: title ?? "确定？", cancelLabel, confirmLabel, danger: true }).then((ok) => {
          if (ok) void onConfirm?.();
        });
      }}
    >
      {children}
    </span>
  );
}

// Named preset colors (matching the Ant Design vocabulary this app's screens
// were already written against — "blue", "gold", "geekblue", etc.) each need a
// readable {bg, text} pastel pair, not the raw CSS color used as a background
// with no matching text-color change — a saturated named color like `blue`
// (#0000FF) behind unchanged dark-slate text is exactly why "可选" looked broken.
const PRESET_COLORS: Record<string, { bg: string; text: string }> = {
  red: { bg: "#fff1f0", text: "#cf1322" },
  orange: { bg: "#fff7e6", text: "#d46b08" },
  gold: { bg: "#fffbe6", text: "#d48806" },
  green: { bg: "#f6ffed", text: "#389e0d" },
  success: { bg: "#f6ffed", text: "#389e0d" },
  cyan: { bg: "#e6fffb", text: "#08979c" },
  blue: { bg: "#e6f4ff", text: "#0958d9" },
  geekblue: { bg: "#f0f5ff", text: "#1d39c4" },
  purple: { bg: "#f9f0ff", text: "#531dab" },
  magenta: { bg: "#fff0f6", text: "#c41d7f" },
  lime: { bg: "#fcffe6", text: "#7cb305" },
  default: { bg: "#f1f5f9", text: "#334155" },
};
function resolvePresetColor(color: any): { bg: string; text: string } {
  if (color && PRESET_COLORS[color]) return PRESET_COLORS[color]!;
  if (typeof color === "string" && color) return { bg: color, text: "#191c1d" };
  return PRESET_COLORS.default!;
}

export function Tag({ children, color, style, onClose, closable }: any) {
  const { bg, text } = resolvePresetColor(color);
  return <span className="inline-flex items-center gap-1.5 rounded-full px-[9px] py-[3px] text-xs font-extrabold" style={{ background: bg, color: text, ...style }}>{children}{closable && <button type="button" onClick={onClose} className="cursor-pointer border-0 bg-transparent p-0 text-sm font-black leading-none text-current">×</button>}</span>;
}

export function Tooltip({ children }: any) {
  return <>{children}</>;
}

type CollapseItem = { key: string; label?: ReactNode; extra?: ReactNode; children?: ReactNode };

export const Collapse: any = ({ items, accordion, ghost, style, children }: {
  items?: CollapseItem[];
  accordion?: boolean;
  ghost?: boolean;
  style?: CSSProperties;
  children?: ReactNode;
}) => {
  const list: CollapseItem[] = items ?? [];
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  if (!items) return <div style={style}>{children}</div>;

  const toggle = (key: string) => {
    setOpenKeys((prev) => {
      const isOpen = prev.includes(key);
      if (accordion) return isOpen ? [] : [key];
      return isOpen ? prev.filter((k) => k !== key) : [...prev, key];
    });
  };

  return (
    <div style={style}>
      {list.map((item) => {
        const isOpen = openKeys.includes(item.key);
        return (
          <div
            key={item.key}
            style={{
              border: ghost ? "none" : "1px solid #E5E7EB",
              borderRadius: ghost ? 0 : 8,
              marginBottom: ghost ? 0 : 8,
              overflow: "hidden",
            }}
          >
            <div
              onClick={() => toggle(item.key)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 12px", cursor: "pointer",
                background: ghost ? "transparent" : "#FAFAFA",
              }}
            >
              <div style={{ flex: 1 }}>{item.label}</div>
              {item.extra}
            </div>
            {isOpen && <div style={{ padding: "0 12px 12px" }}>{item.children}</div>}
          </div>
        );
      })}
    </div>
  );
};
Collapse.Panel = ({ children }: any) => <div>{children}</div>;

export function Slider({ value, onChange, min = 0, max = 100, style }: any) {
  return <input type="range" value={value ?? min} min={min} max={max} onChange={(e) => onChange?.(Number(e.target.value))} className="h-2 w-full cursor-pointer accent-[#f97316]" style={style} />;
}

export function Space({ children, size = 8, style, direction, wrap }: any) {
  return <span className={cx("inline-flex items-center", direction === "vertical" ? "flex-col" : "flex-row", wrap && "flex-wrap")} style={{ gap: `${typeof size === "number" ? size : 8}px`, ...style } as CSSProperties}>{children}</span>;
}
Space.Compact = ({ children, style }: any) => <span className="inline-flex items-center" style={style}>{children}</span>;

export function Row({ children, gutter = 0, style }: any) {
  const gap = Array.isArray(gutter) ? gutter[0] : gutter;
  return <div className="flex flex-wrap" style={{ gap: `${gap}px`, ...style } as CSSProperties}>{children}</div>;
}
export function Col({ children, span, style }: any) {
  return <div className="min-w-0" style={{ flex: span ? `0 0 ${(span / 24) * 100}%` : 1, ...style }}>{children}</div>;
}

export const Typography: any = {
  Title: ({ children, level = 2, style, className = "" }: any) => React.createElement(`h${level}`, { className: cx("m-0 font-black leading-tight text-[#191c1d]", className), style }, children),
  Text: ({ children, strong, type, style, className = "" }: any) => <span className={cx("text-[#111827]", strong && "font-extrabold", type === "secondary" && "text-[#6b7280]", className)} style={style}>{children}</span>,
  Paragraph: ({ children, style, className = "" }: any) => <p className={cx("m-0 text-sm leading-6 text-[#534434]", className)} style={style}>{children}</p>,
};

export const Avatar = ({ children, icon, src, style, size = 32 }: any) => src ? <img src={src} className="shrink-0 overflow-hidden rounded-full object-cover" style={{ width: size, height: size, ...style }} /> : <span className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#f97316] font-extrabold text-white" style={{ width: size, height: size, ...style }}>{icon ?? children}</span>;
// antd status keywords map onto the same preset palette used by Tag.
const BADGE_STATUS_COLOR: Record<string, string> = { success: "green", error: "red", warning: "gold", processing: "blue", default: "default" };

export const Badge = ({ children, count, color, status, text }: any) => {
  // Status-dot variant (color/status + text) — used for things like an
  // application's pending/approved/rejected state, distinct from the
  // notification-count variant below.
  if (color || status || text) {
    const dotColor = resolvePresetColor(color ?? BADGE_STATUS_COLOR[status] ?? "default").text;
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#191c1d]">
        <span className="inline-block size-2 rounded-full" style={{ background: dotColor }} />
        {text}
      </span>
    );
  }
  return <span className="relative inline-flex">{children}{count ? <sup>{count}</sup> : null}</span>;
};
export const Progress = ({ percent = 0, strokeColor = C.brand }: any) => <div className="h-2 overflow-hidden rounded-full bg-[#f1f5f9]"><span className="block h-full rounded-full" style={{ width: `${percent}%`, background: strokeColor }} /></div>;
export const Skeleton = ({ active }: any) => <div className={cx("rounded bg-slate-100", active && "animate-pulse")} />;
export const Descriptions = ({ children }: any) => <div>{children}</div>;
Descriptions.Item = ({ label, children }: any) => <div><b>{label}</b><span>{children}</span></div>;
export const ColorPicker = ({ value, onChange }: any) => <input className="h-10 w-14 cursor-pointer rounded-xl border border-[#e5e7eb] bg-white p-1" type="color" value={typeof value === "string" ? value : "#F97316"} onChange={(e) => onChange?.({ toHexString: () => e.target.value })} />;

type FormRule = AnyRecord | ((form: { getFieldValue: (name: string) => any }) => AnyRecord);

type FormInstance = {
  values: AnyRecord;
  setFieldsValue: (values: AnyRecord) => void;
  setFieldValue: (name: string, value: any) => void;
  getFieldValue: (name: string) => any;
  getFieldsValue: () => AnyRecord;
  getFieldError: (name: string) => string | undefined;
  resetFields: () => void;
  /** Server-side errors, e.g. "phone already in use": shown inline until the field is edited. */
  setFields: (fields: Array<{ name: string; errors?: string[] }>) => void;
  validateFields: () => Promise<AnyRecord>;
  submit: () => void;
  __subscribe?: (fn: () => void) => () => void;
  __onSubmit?: (values: AnyRecord) => void;
  __setFieldRules?: (name: string, rules: FormRule[]) => void;
  __onFieldChange?: (name: string, value: any) => void;
};

/**
 * Evaluates one antd-style rule against a value. Supports the rule shapes
 * actually used across admin-app forms: required/message, len/min/max
 * (string length or numeric bounds when type:"number"), pattern, and
 * dependency-based validator functions (e.g. confirm-PIN matching).
 */
async function evaluateFormRule(rule: FormRule, value: any, form: FormInstance): Promise<string | undefined> {
  const resolved = typeof rule === "function" ? rule({ getFieldValue: form.getFieldValue }) : rule;
  const isEmpty = value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

  if (resolved.required && isEmpty) return resolved.message || "This field is required";
  if (isEmpty) return undefined; // non-required rules don't fire on an empty value

  const isNumberType = resolved.type === "number";
  if (resolved.len != null) {
    const len = isNumberType ? undefined : String(value).length;
    if (len != null && len !== resolved.len) return resolved.message || `Must be exactly ${resolved.len} characters`;
  }
  if (resolved.min != null) {
    if (isNumberType ? Number(value) < resolved.min : String(value).length < resolved.min) {
      return resolved.message || (isNumberType ? `Must be at least ${resolved.min}` : `Must be at least ${resolved.min} characters`);
    }
  }
  if (resolved.max != null) {
    if (isNumberType ? Number(value) > resolved.max : String(value).length > resolved.max) {
      return resolved.message || (isNumberType ? `Must be at most ${resolved.max}` : `Must be at most ${resolved.max} characters`);
    }
  }
  if (resolved.pattern && !resolved.pattern.test(String(value))) return resolved.message || "Invalid format";
  if (resolved.validator) {
    try {
      await resolved.validator(resolved, value);
    } catch (e) {
      return (e as Error)?.message || resolved.message || "Invalid value";
    }
  }
  return undefined;
}

function createForm(): FormInstance {
  const listeners = new Set<() => void>();
  const fieldRules = new Map<string, FormRule[]>();
  const errors: Record<string, string | undefined> = {};
  const form: FormInstance = {
    values: {},
    setFieldsValue(values) { Object.assign(form.values, values); listeners.forEach((fn) => fn()); },
    setFieldValue(name, value) { form.values[name] = value; delete errors[name]; listeners.forEach((fn) => fn()); },
    getFieldValue(name) { return form.values[name]; },
    getFieldsValue() { return { ...form.values }; },
    getFieldError(name) { return errors[name]; },
    setFields(fields) { for (const f of fields) { if (f.errors?.[0]) errors[f.name] = f.errors[0]; else delete errors[f.name]; } listeners.forEach((fn) => fn()); },
    resetFields() { form.values = {}; fieldRules.clear(); for (const k of Object.keys(errors)) delete errors[k]; listeners.forEach((fn) => fn()); },
    __setFieldRules(name, rules) { fieldRules.set(name, rules); },
    async validateFields() {
      const nextErrors: Record<string, string | undefined> = {};
      for (const [name, rules] of fieldRules.entries()) {
        for (const rule of rules) {
          const message = await evaluateFormRule(rule, form.values[name], form);
          if (message) { nextErrors[name] = message; break; }
        }
      }
      for (const k of Object.keys(errors)) delete errors[k];
      Object.assign(errors, nextErrors);
      listeners.forEach((fn) => fn());
      const firstError = Object.values(nextErrors)[0];
      if (firstError) throw new Error(firstError);
      return form.values;
    },
    async submit() {
      try {
        await form.validateFields();
      } catch {
        return; // validation failed — errors are already surfaced inline, don't call onFinish
      }
      form.__onSubmit?.(form.values);
    },
    __subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  };
  return form;
}

const FormContext = createContext<FormInstance | null>(null);

function FormRoot({ children, form, onFinish, onValuesChange, initialValues, style, className = "" }: any) {
  const actualForm = useMemo(() => form ?? createForm(), [form]);
  // Only re-seed the form's values when the form instance or the
  // initialValues object itself changes — NOT on every re-render. `onFinish`
  // is almost always an inline arrow function (a fresh reference every
  // render), so including it here re-ran setFieldsValue after every field
  // edit and silently reverted any unsaved change (e.g. a Switch toggle)
  // back to its original value before the user could save it.
  useMemo(() => { if (initialValues) actualForm.setFieldsValue(initialValues); }, [actualForm, initialValues]);
  actualForm.__onSubmit = onFinish;
  useMemo(() => {
    actualForm.__onFieldChange = onValuesChange
      ? (name: string, value: any) => onValuesChange({ [name]: value }, actualForm.getFieldsValue())
      : undefined;
  }, [actualForm, onValuesChange]);
  return <FormContext.Provider value={actualForm}><form className={cx("m-0", className)} style={style} onSubmit={(e) => { e.preventDefault(); void actualForm.submit(); }}>{children}</form></FormContext.Provider>;
}

function FormItem({ children, name, label, extra, valuePropName = "value", style, className = "", rules, initialValue }: any) {
  const form = useContext(FormContext);
  const [, force] = useState(0);
  React.useEffect(() => form?.__subscribe?.(() => force((n) => n + 1)), [form]);
  // Applies the field-level default exactly once per mount, and only when
  // nothing else (Form-level `initialValues`, or a prior edit) has already
  // set a value — otherwise every Form.Item with `initialValue` (checkboxes,
  // switches, numeric defaults) silently ignored it, since this prop wasn't
  // read anywhere before.
  React.useEffect(() => {
    if (name && initialValue !== undefined && form?.getFieldValue(name) === undefined) {
      form?.setFieldValue(name, initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, form]);
  if (name && rules) form?.__setFieldRules?.(name, rules);
  const child = React.Children.only(children) as any;
  const error = name ? form?.getFieldError?.(name) : undefined;
  const injected = name ? {
    [valuePropName]: form?.getFieldValue(name),
    onChange: (...args: any[]) => {
      const first = args[0];
      const value = first?.target ? (valuePropName === "checked" ? first.target.checked : first.target.value) : first;
      form?.setFieldValue(name, value);
      form?.__onFieldChange?.(name, value);
      child.props?.onChange?.(...args);
    },
  } : {};
  return (
    <label className={cx("mb-3.5 block", className)} style={style}>
      {label && <div className="mb-1.5 text-xs font-extrabold text-[#6b7280]">{label}</div>}
      {React.cloneElement(child, injected)}
      {error && <div className="mt-1.5 text-xs font-bold text-red-600">{error}</div>}
      {extra && <div className="mt-1.5 text-xs text-[#6b7280]">{extra}</div>}
    </label>
  );
}

function useFormHook<T extends AnyRecord>() {
  return React.useMemo(() => [createForm() as FormInstance & { __type?: T }], []);
}

function useWatchHook(name: string, formOrOptions?: FormInstance | { form?: FormInstance }) {
  const contextForm = useContext(FormContext);
  const directForm = formOrOptions && typeof (formOrOptions as FormInstance).getFieldValue === "function"
    ? formOrOptions as FormInstance
    : undefined;
  const optionsForm = formOrOptions && !directForm ? (formOrOptions as { form?: FormInstance }).form : undefined;
  const form = directForm ?? optionsForm ?? contextForm;
  const [value, setValue] = useState(() => form?.getFieldValue?.(name));

  React.useEffect(() => {
    setValue(form?.getFieldValue?.(name));
    return form?.__subscribe?.(() => setValue(form.getFieldValue(name)));
  }, [form, name]);

  return value;
}

export const Form: any = Object.assign(FormRoot, {
  Item: FormItem,
  useForm: useFormHook,
  useWatch: useWatchHook,
});

export const message = {
  success: (text: ReactNode) => pushToast(String(text), "success"),
  // the server's reason for the request that just failed (which field, which rule) is appended to the screen's own "save failed"
  error: (text: ReactNode) => { const why = recentApiError(); pushToast(why ? `${String(text).replace(/[。.!！：:\s]+$/, "")}：${why}` : String(text), "error"); },
  warning: (text: ReactNode) => pushToast(String(text), "error"),
  info: (text: ReactNode) => pushToast(String(text), "info"),
};

export const App: any = ({ children }: any) => <>{children}</>;
App.useApp = () => ({ message, modal: Modal });

export function Upload({ children, beforeUpload, customRequest, onChange }: any) {
  return <label className="inline-flex size-[104px] cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-[#cbd5e1] bg-[#f8fafc]">{children}<input type="file" hidden accept="image/png,image/jpeg,image/webp,image/gif,image/avif" onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (!file) return; if (beforeUpload?.(file) === false) return; customRequest?.({ file, onSuccess: () => undefined, onError: () => undefined }); onChange?.({ file, fileList: [file] }); }} /></label>;
}
Upload.Dragger = Upload;
